/* eslint-disable react-hooks/exhaustive-deps */

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  MessageCircle,
  Search,
  SlidersHorizontal,
  Smartphone,
  Wallet,
  Users,

} from "lucide-react";

import heroSaree from "../assets/hero-saree.avif";
import sareeCraft from "../assets/craft.avif";
import { products } from "../data/products";

const ENQUIRY_ENDPOINT = "https://script.google.com/macros/s/AKfycbwB0Yl2YlmXLxqe12_u07cbtPuvAskV0G1hvG6WzvvydR2KQHJKL1wpQq4kgokjE7CJ/exec";

const reveal = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const steps = [
  {
    n: "1",
    title: "Subscribe for ₹499/month",
    text: "Choose the monthly subscription to access the Vasthra reseller selling journey. Review the subscription terms before paying.",
  },
  {
    n: "2",
    title: "Select your sarees",
    text: "Browse the collection and choose sarees you would love to share with your customers.",
  },
  {
    n: "3",
    title: "Set your price",
    text: "Understand the product cost and your applicable margin before sharing the saree.",
  },
  {
    n: "4",
    title: "Share everywhere",
    text: "Share products with friends, family and customers through WhatsApp and social media.",
  },
  {
    n: "5",
    title: "Build customer relationships",
    text: "Share saree collections with interested customers and provide clear product and order details.",
  },
  {
    n: "6",
    title: "Withdraw via UPI",
    text: "Check your available earnings and follow the applicable payout and withdrawal process.",
  },
];

const faqs = [
  {
    q: "How much does the subscription cost?",
    a: "The reseller subscription is ₹499 per month. Review the current subscription, renewal and cancellation terms before paying."
  },
  {
    q: "Do I need to buy stock?",
    a: "The reseller model is designed to let you explore and share listed products. Confirm product availability, order handling and fulfilment terms before accepting orders.",
  },
  {
    q: "How much can I earn?",
    a: "Your earnings depend on completed orders, your applicable margin, demand and other costs. Any earnings figures shown are examples, not guaranteed income.",
  },
  {
    q: "When do I get my money?",
    a: "Payout timing depends on the platform's applicable payment and withdrawal policy. Confirm the current terms before getting started.",
  },
  {
    q: "Can I list my own sarees?",
    a: "Yes, shop owners and loom owners can enquire about listing eligible products. Onboarding and listing terms must be confirmed with Vasthra.",
  },
  {
    q: "What if I need help?",
    a: "Use the enquiry form below to send your details to the Vasthra team. Your enquiry is recorded for follow-up.",
  },
  {
    q: "How does the referral programme work?",
    a: "Referral rewards depend on the active programme, eligibility requirements and terms. Confirm the current reward structure before referring anyone.",
  },
];

function SectionTitle({ eyebrow, title, description }) {
  return (
    <motion.div variants={reveal} className="mb-9">
      <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em] text-amber-700">
        {eyebrow}
      </p>
      <h2 className="font-serif text-3xl leading-tight tracking-tight text-rose-950 sm:text-4xl lg:text-[2.65rem]">
        {title}
      </h2>
      {description && (
        <p className="mt-4 max-w-2xl text-base leading-7 text-stone-600">
          {description}
        </p>
      )}
    </motion.div>
  );
}

function Section({ children, id, className = "" }) {
  return (
    <motion.section
      id={id}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.12 }}
      variants={stagger}
      className={`scroll-mt-24 px-5 py-14 sm:px-8 lg:px-12 lg:py-16 ${className}`}
    >
      <div className="mx-auto max-w-6xl">{children}</div>
    </motion.section>
  );
}

const sareeFilters = [
  { label: "All Sarees", value: "all" },
  { label: "Silk", value: "silk" },
  { label: "Cotton", value: "cotton" },
  { label: "Designer", value: "designer" },
  { label: "Bridal", value: "bridal" },
  { label: "Traditional", value: "traditional" },
];

function getProductSearchText(product) {
  const tags = Array.isArray(product.tags) ? product.tags.join(" ") : product.tags || "";
  const categories = Array.isArray(product.categories)
    ? product.categories.join(" ")
    : product.categories || "";
  return [
    product.name,
    product.title,
    product.category,
    product.categoryName,
    product.type,
    product.material,
    product.fabric,
    product.style,
    tags,
    categories,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function SareeCard({ product, index }) {
  const name = product.name || product.title || "Saree";
  const image =
    product.image ||
    product.imageUrl ||
    product.photo ||
    product.thumbnail ||
    (Array.isArray(product.images) ? product.images[0] : "");

  return (
    <motion.article
      variants={reveal}
      whileHover={{ y: -5 }}
      className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-lg"
    >
      <div className="relative aspect-[4/4.5] overflow-hidden bg-[#f0e3dc]">
        <img
          src={image || (index % 2 === 0 ? heroSaree : sareeCraft)}
          alt={name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2.5 py-1 text-[9px] font-semibold text-rose-950">
          Saree collection
        </span>
      </div>

      <div className="p-3">
        <h3 className="line-clamp-2 min-h-10 font-serif text-sm text-rose-950">
          {name}
        </h3>
        <p className="mt-1 text-[10px] leading-4 text-stone-500">
          Explore this style and share it with your customers.
        </p>
        <a
          href="/signup"
          className="mt-3 flex items-center justify-center gap-1 rounded-md bg-rose-800 px-2 py-2.5 text-center text-[10px] font-semibold text-white transition hover:bg-rose-950"
        >
          Start Selling This Saree <ArrowRight size={12} />
        </a>
      </div>
    </motion.article>
  );
}

export default function Home() {
  const [activeFaq, setActiveFaq] = useState(-1);
  const [activeSareeFilter, setActiveSareeFilter] = useState("all");
  const [sareeSearch, setSareeSearch] = useState("");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    interest: "Reseller registration",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  const listedProducts = Array.isArray(products) ? products : [];
  const visibleProducts = useMemo(() => {
    const query = sareeSearch.trim().toLowerCase();
    return listedProducts.filter((product) => {
      const text = getProductSearchText(product);
      const matchesSearch = !query || text.includes(query);
      const matchesFilter =
        activeSareeFilter === "all" ||
        text.includes(activeSareeFilter) ||
        (activeSareeFilter === "traditional" &&
          /ethnic|classic|handloom|heritage|banarasi|kanjivaram|kanjeevaram/.test(text)) ||
        (activeSareeFilter === "designer" &&
          /design|designer|contemporary|embroidered|embroidery/.test(text)) ||
        (activeSareeFilter === "bridal" &&
          /bridal|wedding|marriage|occasion/.test(text));
      return matchesSearch && matchesFilter;
    });
  }, [listedProducts, activeSareeFilter, sareeSearch]);
  const updateForm = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const submitEnquiry = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const name = form.name.trim();
    const phone = form.phone.trim();
    const interest = form.interest.trim();
    const message = form.message.trim();

    if (!name || !phone || !interest) {
      setSubmitStatus({
        type: "error",
        message: "Please enter your name, phone number and enquiry type.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      // Apps Script is cross-origin. A text/plain POST avoids a CORS preflight.
      // With no-cors, the browser cannot read the response; check the Sheet
      // after the first live submission to verify the end-to-end connection.
      await fetch(ENQUIRY_ENDPOINT, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify({ name, phone, interest, message }),
      });

      setSubmitStatus({
        type: "success",
        message:
          "Your enquiry has been sent. Our team will review it and get back to you. If you do not hear from us, please contact Vasthra directly.",
      });
      setForm({
        name: "",
        phone: "",
        interest: "Reseller registration",
        message: "",
      });
    } catch (error) {
      console.error("Enquiry submission failed:", error);
      setSubmitStatus({
        type: "error",
        message:
          "We could not send your enquiry right now. Please try again or contact Vasthra directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="overflow-hidden bg-[#fffdf8] text-stone-900">
      {/* HERO — editorial split layout with a real saree image on the right */}
      <section
        id="home"
        className="relative isolate scroll-mt-20 overflow-hidden bg-[#510b20] text-white"
      >
        <div className="absolute inset-0 -z-20">
          <motion.img
            src={heroSaree}
            alt="Traditional saree collection"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-cover object-center opacity-35"
          />
        </div>
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#390615]/95 via-[#510b20]/90 to-[#510b20]/55" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/[0.06] via-transparent to-[#2d0610]/45" />
        <div className="pointer-events-none absolute -right-20 -top-28 -z-10 h-80 w-80 rounded-full border border-amber-200/15" />
        <div className="pointer-events-none absolute -right-6 -top-14 -z-10 h-56 w-56 rounded-full border border-amber-200/10" />

        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:px-12 lg:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.13, delayChildren: 0.12 } },
            }}
            className="relative z-10"
          >
            <motion.span
              variants={{
                hidden: { opacity: 0, y: 16 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.65 } },
              }}
              className="inline-flex items-center gap-2 rounded-full border border-amber-200/35 bg-white/10 px-4 py-2 text-xs font-medium tracking-wide text-amber-100 backdrop-blur-md sm:text-sm"
            >
              <span className="h-2 w-2 rounded-full bg-amber-300 shadow-[0_0_12px_rgba(252,211,77,0.8)]" />
              Welcome to Vasthra
            </motion.span>

            <motion.h1
              variants={{
                hidden: { opacity: 0, y: 26 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
              }}
              className="mt-7 max-w-2xl font-serif text-5xl font-medium leading-[1.04] tracking-[-0.035em] sm:text-6xl lg:text-[4.35rem] xl:text-[4.75rem]"
            >
              Your own saree business,
              <br />
              <span className="mt-1 inline-block italic text-amber-300">
                run from your phone.
              </span>
            </motion.h1>

            <motion.p
              variants={{
                hidden: { opacity: 0, y: 18 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.65 } },
              }}
              className="mt-6 max-w-xl text-base leading-8 text-white/85 sm:text-lg sm:leading-8"
            >
            
              Turn your love for sarees into a business of your own. Discover beautiful
              styles, share your favourite collections with customers, and take the
              first step towards building your saree-selling journey with Vasthra.
            </motion.p>

            <motion.div
              variants={{
                hidden: { opacity: 0, y: 18 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
              }}
              className="mt-8 flex flex-wrap gap-3 sm:gap-4"
            >
              <motion.a
                href="/signup"
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 rounded-md bg-amber-300 px-5 py-3.5 text-sm font-bold text-rose-950 shadow-lg shadow-black/20 transition-colors hover:bg-amber-200 sm:px-6 sm:py-4 sm:text-base"
              >
                Start Your Business <ArrowRight size={17} />
              </motion.a>
              <motion.a
                href="#how-it-works"
                whileHover={{ y: -3, backgroundColor: "rgba(255,255,255,0.12)" }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center gap-2 rounded-md border border-white/40 bg-white/[0.06] px-5 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-colors sm:px-6 sm:py-4 sm:text-base"
              >
                See How It Works <ArrowUpRight size={17} />
              </motion.a>
            </motion.div>

            <motion.div
              variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.7 } } }}
              className="mt-7 flex flex-wrap gap-x-5 gap-y-3 text-xs font-medium text-amber-50/90 sm:text-sm"
            >
              <span className="flex items-center gap-2"><Check size={15} className="text-amber-300" /> Explore saree collections</span>
              <span className="flex items-center gap-2"><Check size={15} className="text-amber-300" /> Share products online</span>
              <span className="flex items-center gap-2"><Check size={15} className="text-amber-300" /> Build customer relationships</span>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 32, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[440px] lg:max-w-none"
          >
            <div className="absolute -inset-3 rounded-[2rem] border border-amber-200/25" />
            <div className="relative overflow-hidden rounded-[1.65rem] border border-white/20 bg-white/10 p-2 shadow-2xl shadow-black/35 backdrop-blur-sm">
              <div className="relative overflow-hidden rounded-[1.2rem] bg-[#ead8c5]">
                <motion.img
                  src={sareeCraft}
                  alt="Beautiful saree style from Vasthra"
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.8, ease: "easeOut" }}
                  className="h-[360px] w-full object-cover object-center sm:h-[430px] lg:h-[490px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2c0610]/75 via-transparent to-white/10" />
                <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-200">
                    A world of drapes
                  </p>
                  <h2 className="mt-2 max-w-sm font-serif text-2xl leading-tight text-white sm:text-3xl">
                    Timeless tradition. Your next opportunity.
                  </h2>
                  <a
                    href="#collection"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-white transition hover:text-amber-200"
                  >
                    Explore the collection <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="h-1 bg-gradient-to-r from-amber-500 via-amber-200 to-amber-500" />
      </section>

      {/* MADE FOR MOBILE */}
      <Section id="mobile" className="text-center">
        <SectionTitle
          eyebrow="Made for everyday life"
          title="Made for anyone with a mobile phone"
          description="You don't need a big shop to explore online saree selling. Start from your phone and share products with people you know."
        />

        <div className="grid gap-3 text-left sm:grid-cols-2 lg:grid-cols-4">
          {[
            [Smartphone, "Students", "Explore flexible selling around your schedule."],
            [Wallet, "Housewives", "Learn about a saree business from home."],
            [Users, "Working professionals", "Explore another way to use your network."],
            [MessageCircle, "Anyone with a phone", "Share products through everyday conversations."],
          ].map(([Icon, title, text]) => (
            <motion.div
              key={title}
              variants={reveal}
              className="rounded-lg border border-stone-200 bg-[#fffdf8] p-4"
            >
              <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-md bg-[#f4e6df] text-rose-900">
                <Icon size={17} />
              </div>
              <h3 className="font-serif text-base text-rose-950">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-stone-600">{text}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how-it-works" className="bg-[#f8f3eb]">
        <SectionTitle
          eyebrow="Your journey"
          title="How it works"
          description="Start your journey, explore sarees, share products and learn how the selling process works."
        />

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((step) => (
            <motion.div
              key={step.n}
              variants={reveal}
              className="rounded-lg border border-stone-100 bg-white p-4 transition hover:-translate-y-1 hover:shadow-md"
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-950 text-sm font-bold text-white">
                {step.n}
              </span>
              <h3 className="mt-3 font-serif text-base text-rose-950">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-6 text-stone-600">
                {step.text}
              </p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* WHERE THE CUSTOMER'S MONEY GOES */}
      <Section id="payments">
        <div className="grid items-center gap-8 md:grid-cols-2">
          <motion.div variants={reveal}>
            <SectionTitle
              eyebrow="Understand the order"
              title="Where the customer's money goes"
              description="Understand the product price, your applicable margin and any delivery or other charges before confirming an order."
            />

            <ul className="space-y-3 text-sm leading-6 text-stone-600">
              {[
                "The product cost should be clear before an order is placed.",
                "Your applicable margin depends on the confirmed selling terms.",
                "Delivery charges depend on the order details.",
                "Check the final payable amount before confirming a purchase.",
              ].map((text) => (
                <li key={text} className="flex gap-2">
                  <Check size={15} className="shrink-0 text-emerald-700" />
                  {text}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            variants={reveal}
            className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm"
          >
            <h3 className="font-serif text-lg text-rose-950">
              Customer pays ₹1,340
            </h3>

            <div className="mt-5 overflow-hidden rounded-md">
              <div className="flex h-8">
                <div className="flex w-[55%] items-center justify-center bg-rose-950 text-[10px] font-bold text-white">
                  Product cost
                </div>
                <div className="flex flex-1 items-center justify-center bg-emerald-600 text-[10px] font-bold text-white">
                  Margin
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                ["₹1,220", "Example product cost"],
                ["₹120", "Example margin"],
                ["₹0", "Other charges assumed"],
              ].map(([value, label]) => (
                <div key={label} className="rounded-md bg-[#faf6ef] p-3">
                  <p className="font-serif text-lg text-rose-950">{value}</p>
                  <p className="mt-1 text-[9px] leading-4 text-stone-500">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            <p className="mt-3 text-[10px] leading-4 text-stone-500">
              Example only. Actual product costs, margins and charges must be
              confirmed from the order details.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* SAREE COLLECTION */}
      <Section id="collection">
        <SectionTitle
          eyebrow="Explore the collection"
          title="All types of sarees"
          description="Explore traditional, designer, festive and occasion-wear styles. Choose a saree you love and start your selling journey."
        />

        <div className="mb-7 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-rose-800">
                <SlidersHorizontal size={14} /> Find your style
              </p>
              <p className="mt-1 text-sm text-stone-500">
                Filter the collection by fabric or occasion.
              </p>
            </div>
            <label className="relative block w-full md:max-w-xs">
              <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="search"
                value={sareeSearch}
                onChange={(event) => setSareeSearch(event.target.value)}
                placeholder="Search sarees..."
                aria-label="Search sarees"
                className="w-full rounded-full border border-stone-200 bg-[#fcfaf7] py-3 pl-10 pr-4 text-sm outline-none transition focus:border-rose-700 focus:ring-2 focus:ring-rose-100"
              />
            </label>
          </div>
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filter sarees by style">
            {sareeFilters.map((filter) => {
              const selected = activeSareeFilter === filter.value;
              return (
                <button
                  key={filter.value}
                  type="button"
                  onClick={() => setActiveSareeFilter(filter.value)}
                  aria-pressed={selected}
                  className={`shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition sm:text-sm ${
                    selected
                      ? "border-rose-900 bg-rose-900 text-white shadow-sm"
                      : "border-stone-200 bg-white text-stone-600 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-900"
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>

        {visibleProducts.length > 0 ? (
          <>
            <p className="mb-3 text-xs text-stone-500">
              Showing {visibleProducts.length} {visibleProducts.length === 1 ? "style" : "styles"}
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {visibleProducts.map((product, index) => (
                <SareeCard
                  key={product.id || product.slug || index}
                  product={product}
                  index={index}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-5 py-12 text-center">
            <p className="font-serif text-xl text-rose-950">No matching sarees found</p>
            <p className="mt-2 text-sm text-stone-500">
              Try another filter or search with a different keyword.
            </p>
            <button
              type="button"
              onClick={() => {
                setActiveSareeFilter("all");
                setSareeSearch("");
              }}
              className="mt-4 rounded-full bg-rose-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-950"
            >
              Show all sarees
            </button>
          </div>
        )}
      </Section>

      {/* PAYOUT & SUPPORT */}
      <Section className="bg-[#f8f3eb]">
        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              icon: Wallet,
              title: "Earnings & withdrawals",
              points: [
                "Review your available earnings and withdrawal eligibility.",
                "Check the current payout process and any applicable fees.",
                "Check eligibility and minimum withdrawal requirements.",
                "Confirm payout timing in the applicable policy.",
              ],
            },
            {
              icon: MessageCircle,
              title: "Help when you need it",
              points: [
                "Ask questions about the selling process.",
                "Get clarification about products and orders.",
                "Enquire about registration and referrals.",
                "Contact the team using the enquiry form below.",
              ],
            },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                variants={reveal}
                className="rounded-xl bg-white p-5"
              >
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-[#f6e8df] p-2 text-rose-950">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-serif text-xl text-rose-950">
                    {item.title}
                  </h3>
                </div>

                <ul className="mt-5 space-y-3">
                  {item.points.map((point) => (
                    <li
                      key={point}
                      className="flex gap-2 text-sm leading-6 text-stone-600"
                    >
                      <Check size={14} className="shrink-0 text-emerald-700" />
                      {point}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* SHOP OWNERS */}
      <Section id="shop-owners">
        <div className="grid items-center gap-7 md:grid-cols-2">
          <motion.div variants={reveal}>
            <SectionTitle
              eyebrow="For business owners"
              title="Own a saree shop or loom?"
              description="Bring your saree collection to a wider audience. Enquire about listing your products and joining Vasthra."
            />
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-md bg-rose-900 px-5 py-3 text-xs font-semibold text-white transition hover:bg-rose-950"
            >
              Enquire as a shop owner <ArrowRight size={15} />
            </a>
          </motion.div>

          <motion.div variants={reveal} className="space-y-4">
            {[
              "Enquire about listing your sarees.",
              "Learn about onboarding and listing terms.",
              "Discuss product details and availability.",
              "Understand the process before joining.",
            ].map((point) => (
              <p
                key={point}
                className="flex gap-2 text-sm leading-6 text-stone-600"
              >
                <Check size={15} className="shrink-0 text-emerald-700" />
                {point}
              </p>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="bg-[#f8f3eb]">
        <div className="mx-auto max-w-3xl">
          <SectionTitle
            eyebrow="Need to know"
            title="Questions people ask"
            description="Answers to common questions about getting started with Vasthra."
          />

          <div className="space-y-2">
            {faqs.map((faq, index) => {
              const open = activeFaq === index;

              return (
                <motion.div
                  key={faq.q}
                  variants={reveal}
                  className="overflow-hidden rounded-md border border-stone-200 bg-white"
                >
                  <button
                    type="button"
                    onClick={() => setActiveFaq(open ? -1 : index)}
                    aria-expanded={open}
                    className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left text-sm font-semibold text-rose-950"
                  >
                    {faq.q}
                    <ChevronDown
                      size={15}
                      className={`shrink-0 transition-transform ${
                        open ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <motion.div
                    initial={false}
                    animate={{
                      height: open ? "auto" : 0,
                      opacity: open ? 1 : 0,
                    }}
                    className="overflow-hidden"
                  >
                    <p className="px-4 pb-4 text-sm leading-7 text-stone-600">
                      {faq.a}
                    </p>
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ENQUIRY */}
      <Section id="contact">
        <div className="grid gap-8 md:grid-cols-2">
          <motion.div variants={reveal}>
            <SectionTitle
              eyebrow="Let's connect"
              title="Have a question? Send an enquiry."
              description="Tell us whether you want to become a reseller, explore sarees or list your shop. Your enquiry will be sent to the Vasthra team for follow-up."
            />

            <ul className="space-y-3">
              {[
                "Ask about the saree collection and available products.",
                "Enquire about the reseller registration process.",
                "Discuss shop and loom product listings.",
              ].map((point) => (
                <li
                  key={point}
                  className="flex gap-2 text-sm leading-6 text-stone-600"
                >
                  <Check size={14} className="shrink-0 text-emerald-700" />
                  {point}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.form
            variants={reveal}
            onSubmit={submitEnquiry}
            className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm"
          >
            <h3 className="font-serif text-xl text-rose-950">Enquiry form</h3>
            <p className="mt-2 text-[10px] leading-5 text-stone-500">
              Fill in your details and submit your enquiry directly to Vasthra.
            </p>

            {submitStatus.message && (
              <div
                role="status"
                aria-live="polite"
                className={`mt-4 rounded-md border px-3 py-3 text-xs leading-5 ${
                  submitStatus.type === "success"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-red-200 bg-red-50 text-red-800"
                }`}
              >
                {submitStatus.message}
              </div>
            )}

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <label className="text-[10px] font-semibold text-stone-700">
                Name
                <input
                  name="name"
                  value={form.name}
                  onChange={updateForm}
                  required
                  placeholder="Your name"
                  className="mt-2 w-full rounded-md border border-stone-200 px-3 py-3 text-xs font-normal outline-none focus:border-rose-800"
                />
              </label>

              <label className="text-[10px] font-semibold text-stone-700">
                Mobile / WhatsApp number
                <input
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  pattern="[+0-9()\\s-]{7,20}"
                  value={form.phone}
                  onChange={updateForm}
                  required
                  placeholder="Your phone number"
                  className="mt-2 w-full rounded-md border border-stone-200 px-3 py-3 text-xs font-normal outline-none focus:border-rose-800"
                />
              </label>
            </div>

            <label className="mt-4 block text-[10px] font-semibold text-stone-700">
              I'm interested in
              <select
                name="interest"
                value={form.interest}
                onChange={updateForm}
                className="mt-2 w-full rounded-md border border-stone-200 bg-white px-3 py-3 text-xs font-normal outline-none focus:border-rose-800"
              >
                <option>Reseller registration</option>
                <option>Shop or loom listing</option>
                <option>Saree products</option>
                <option>UPI withdrawals</option>
                <option>Other enquiry</option>
              </select>
            </label>

            <label className="mt-4 block text-[10px] font-semibold text-stone-700">
              Your message
              <textarea
                name="message"
                value={form.message}
                onChange={updateForm}
                rows={3}
                placeholder="Ask anything about joining, listing or products..."
                className="mt-2 w-full resize-y rounded-md border border-stone-200 px-3 py-3 text-xs font-normal outline-none focus:border-rose-800"
              />
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-emerald-700 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Sending enquiry...
                </>
              ) : (
                <>
                  <Check size={16} /> Submit enquiry
                </>
              )}
            </button>

            <p className="mt-3 text-[9px] leading-4 text-stone-400">
              Your details are sent to the Vasthra enquiry spreadsheet for follow-up.
              Please avoid including sensitive personal or payment information.
            </p>
          </motion.form>
        </div>
      </Section>

      {/* FINAL CTA — LIGHT EDITORIAL FINISH; FOOTER IS IN App.jsx */}
      <section className="relative isolate overflow-hidden border-t border-[#ead9c7] bg-[#fffdf8] px-5 py-16 text-rose-950 sm:px-8 sm:py-20 lg:py-24">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-px bg-gradient-to-r from-transparent via-amber-500/60 to-transparent" />
        <div className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#f5e9dc]/70 blur-3xl" />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-4xl text-center"
        >
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-amber-700 sm:text-sm">
            Your next chapter starts here
          </p>
          <h2 className="mx-auto mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-rose-950 sm:text-5xl lg:text-6xl">
            Your saree store can be live in five minutes.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-stone-600 sm:text-lg">
            Ready to start? Explore the registration process, discover sarees and take the first step towards your saree-selling journey.
          </p>
          <motion.a
            href="/signup"
            whileHover={{ y: -3, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="mt-8 inline-flex items-center gap-2 rounded-md bg-rose-900 px-6 py-4 text-sm font-bold text-white shadow-lg shadow-rose-950/10 transition-colors hover:bg-rose-950 sm:text-base"
          >
            Start your saree business <ArrowUpRight size={17} />
          </motion.a>
          <p className="mt-4 text-xs text-stone-500">
            Discover the collection. Learn the process. Start your journey.
          </p>
        </motion.div>
      </section>
    </main>
  );
}
